var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import { useAuth0 } from "@auth0/auth0-react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import { IncidentImpact, IncidentIssue, isIncidentActive, } from "ee-nestjs-interfaces";
import { apiFetch, Dropdown, StaticTable, StyledButton, StyledButtonStyle, TextInput, } from "ee-react-components";
import { process } from "../../Process";
import { IncidentEditDialog } from "./IncidentEditDialog";
import { TicketLink } from "./TicketLink";
import { IMPACT_LABELS, ISSUE_LABELS, impactLabel, issueLabel, } from "./IncidentLabels";
var IMPACT_VALUES = Object.values(IncidentImpact);
var ISSUE_VALUES = Object.values(IncidentIssue);
export var IncidentsPane = function (_a) {
    var _b, _c, _d, _e;
    var siteId = _a.siteId, showSite = _a.showSite, canWrite = _a.canWrite, chargers = _a.chargers, powernodes = _a.powernodes;
    var _f = useState(false), showCreate = _f[0], setShowCreate = _f[1];
    var closeCreate = useCallback(function () { return setShowCreate(false); }, []);
    var openCreate = useCallback(function () { return setShowCreate(true); }, []);
    var _g = useAuth0(), getAccessTokenSilently = _g.getAccessTokenSilently, isLoading = _g.isLoading, isAuthenticated = _g.isAuthenticated;
    // The filters are derived from the query string rather than held in state, so
    // the URL is the single source of truth: pasting one reproduces the search,
    // and the back button walks it. The query string uses the list endpoint's own
    // parameter names, so the controls read it and the request forwards it — no
    // filter is renamed or reshaped in between.
    var _h = useSearchParams(), searchParams = _h[0], setSearchParams = _h[1];
    var ongoingOnly = (_b = searchParams.get("ongoingOnly")) !== null && _b !== void 0 ? _b : "";
    var impact = (_c = searchParams.get("impact")) !== null && _c !== void 0 ? _c : undefined;
    var issue = (_d = searchParams.get("issue")) !== null && _d !== void 0 ? _d : undefined;
    var opsTicket = (_e = searchParams.get("opsTicket")) !== null && _e !== void 0 ? _e : "";
    var excludeGlobal = searchParams.get("excludeGlobal") === "true";
    // `replace` so typing in the ticket box does not push a history entry per
    // keystroke; only the resulting URL matters. A blank value is deleted rather
    // than set, so a default filter leaves no trace in the URL and a shared link
    // carries only what was actually chosen.
    var setFilter = useCallback(function (key, value) {
        setSearchParams(function (previous) {
            var next = new URLSearchParams(previous);
            if (value)
                next.set(key, value);
            else
                next.delete(key);
            return next;
        }, { replace: true });
    }, [setSearchParams]);
    var setOngoingOnly = useCallback(function (value) { return setFilter("ongoingOnly", value); }, [setFilter]);
    var setImpact = useCallback(function (value) { return setFilter("impact", value); }, [setFilter]);
    var setIssue = useCallback(function (value) { return setFilter("issue", value); }, [setFilter]);
    var setOpsTicket = useCallback(function (value) { return setFilter("opsTicket", value); }, [setFilter]);
    // Undefined when unchecked, so the default leaves no trace in the URL.
    var setExcludeGlobal = useCallback(function (value) { return setFilter("excludeGlobal", value ? "true" : undefined); }, [setFilter]);
    // The filters go to the server, so the query string is part of the SWR key —
    // changing a filter refetches rather than filtering a stale page client-side.
    // `siteId` is the one thing not already in the URL: it scopes the page, and
    // is omitted for the NOC-wide list where every site is in scope.
    var listUrl = useMemo(function () {
        var query = new URLSearchParams(searchParams);
        if (siteId != null)
            query.set("siteId", String(siteId));
        query.sort();
        return "".concat(process.env.NOC_SVC_URL, "/incidents?").concat(query.toString());
    }, [searchParams, siteId]);
    var getToken = useCallback(function () {
        return getAccessTokenSilently({
            authorizationParams: { audience: process.env.AUTH0_AUDIENCE },
        });
    }, [getAccessTokenSilently]);
    // Immutable, and only when the Site column is on: site names are the
    // slowest-moving thing on this screen, and there is no reason to refetch the
    // fleet listing as filters change.
    var sites = useSWRImmutable(!showSite || isLoading || !isAuthenticated
        ? null
        : "".concat(process.env.NOC_SVC_URL, "/sites/listing"), function (url) {
        return apiFetch(url, { method: "GET", callbacks: { getToken: getToken } });
    }).data;
    var siteNames = useMemo(function () { return new Map((sites !== null && sites !== void 0 ? sites : []).map(function (site) { return [site.id, site.name]; })); }, [sites]);
    var _j = useSWR(isLoading || !isAuthenticated ? null : listUrl, function (url) {
        return apiFetch(url, { method: "GET", callbacks: { getToken: getToken } });
    }), incidents = _j.data, error = _j.error, mutate = _j.mutate;
    var statusItems = useMemo(function () { return [
        { name: "All", value: "", selectedLabel: "All incidents" },
        { name: "Active only", value: "true" },
    ]; }, []);
    var impactItems = useMemo(function () { return __spreadArray([
        { name: "All", value: undefined, selectedLabel: "All impacts" }
    ], IMPACT_VALUES.map(function (value) { return ({
        name: IMPACT_LABELS[value],
        value: value,
    }); }), true); }, []);
    var issueItems = useMemo(function () { return __spreadArray([
        { name: "All", value: undefined, selectedLabel: "All issues" }
    ], ISSUE_VALUES.map(function (value) { return ({
        name: ISSUE_LABELS[value],
        value: value,
    }); }), true); }, []);
    // StaticTable memoizes its columns on this array's identity, so building it
    // inline would rebuild the table on every keystroke in the filter box.
    var columns = useMemo(function () { return __spreadArray(__spreadArray([], (showSite
        ? [
            {
                header: "Site",
                accessorFn: function (row) {
                    var _a;
                    return row.siteId == null
                        ? "All sites"
                        : (_a = siteNames.get(row.siteId)) !== null && _a !== void 0 ? _a : "Site ".concat(row.siteId);
                },
            },
        ]
        : []), true), [
        {
            header: "Start",
            accessorFn: function (row) { return new Date(row.beginning).toLocaleString(); },
        },
        {
            header: "End",
            accessorFn: function (row) {
                return row.end ? new Date(row.end).toLocaleString() : "Ongoing";
            },
            // Reads the row rather than sniffing the rendered string, so the cell
            // and the sort/filter value stay independent. Red covers both live
            // shapes: no end at all, and an end that has not arrived yet — the
            // latter keeps its timestamp on screen, since when it closes matters.
            cell: function (info) {
                var end = info.row.original.end;
                var className = isIncidentActive(end) ? "text-red" : undefined;
                return (React.createElement("label", { className: className }, end ? info.getValue() : "ONGOING"));
            },
        },
        { header: "Title", accessorFn: function (row) { return row.title; } },
        {
            header: "Impact",
            // A site view has no Site column to say where an incident belongs, so a
            // global one would be indistinguishable from this site's own — the tag
            // is what keeps them apart. The fleet-wide view already says "All
            // sites" in its own column and does not need it repeated.
            accessorFn: function (row) {
                var labels = row.impact.map(impactLabel).join(", ");
                return !showSite && row.siteId == null
                    ? "".concat(labels, " (Global)")
                    : labels;
            },
        },
        {
            header: "Issue",
            accessorFn: function (row) { return row.issue.map(issueLabel).join(", "); },
        },
        {
            header: "OPS Ticket",
            accessorFn: function (row) { return row.opsTicket; },
            // Links to Jira; the row's own navigation to the incident still works
            // everywhere else in the cell.
            cell: function (info) { return React.createElement(TicketLink, { ticket: info.getValue() }); },
        },
        {
            header: "Chargers",
            accessorFn: function (row) {
                return row.chargerIds.length > 0 ? String(row.chargerIds.length) : "";
            },
        },
        // Read from the incident's newest change-log entry, so it always agrees
        // with the audit log. Null only if an incident somehow has no log at all.
        {
            header: "Last Edited By",
            accessorFn: function (row) { var _a; return (_a = row.updatedBy) !== null && _a !== void 0 ? _a : "-"; },
        },
    ], false); }, [showSite, siteNames]);
    // The form has no idea what it is filing against; the pane does. `siteId ??
    // null` is the whole scope decision: the site tab files against its site, the
    // fleet-wide view files a global incident, which is what a null site means.
    // Any ApiError thrown here propagates to the dialog, which shows the
    // service's own message ("Charger(s) 4 do not belong to site 2") — things the
    // form cannot pre-check.
    var submitCreate = useCallback(function (values) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, apiFetch("".concat(process.env.NOC_SVC_URL, "/incidents"), {
                        method: "POST",
                        body: __assign({ siteId: siteId !== null && siteId !== void 0 ? siteId : null }, values),
                        callbacks: { getToken: getToken },
                    })];
                case 1:
                    _a.sent();
                    return [4 /*yield*/, mutate()];
                case 2:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [siteId, getToken, mutate]);
    // Recomputed per fetch rather than on a timer: an incident crossing its end
    // date mid-view can wait for the next revalidation, and a ticking clock in
    // render would defeat the memo for no operator benefit.
    var activeCount = useMemo(function () { return (incidents !== null && incidents !== void 0 ? incidents : []).filter(function (i) { return isIncidentActive(i.end); }).length; }, [incidents]);
    return (React.createElement("div", { className: "flex flex-col gap-y-[32px]" },
        React.createElement("div", { className: "flex flex-row gap-x-4 items-center" },
            React.createElement("label", { className: "font-N5 text-white" }, "Incidents"),
            activeCount > 0 && (React.createElement("label", { className: "font-N8 text-red" }, "".concat(activeCount, " active")))),
        React.createElement("div", { className: "flex flex-row gap-x-[32px] items-center flex-wrap gap-y-[16px]" },
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "STATUS"),
                React.createElement("span", { className: "relative w-full" },
                    React.createElement(Dropdown, { items: statusItems, onClick: setOngoingOnly, currentSelection: ongoingOnly }))),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "FILTER BY IMPACT"),
                React.createElement("span", { className: "relative w-full" },
                    React.createElement(Dropdown, { items: impactItems, onClick: setImpact, currentSelection: impact }))),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "FILTER BY ISSUE"),
                React.createElement("span", { className: "relative w-full" },
                    React.createElement(Dropdown, { items: issueItems, onClick: setIssue, currentSelection: issue }))),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "OPS TICKET"),
                React.createElement(TextInput, { placeholder: "OPS-1234, 1234 or a link", value: opsTicket, onChange: function (e) { return setOpsTicket(e.target.value); } })),
            React.createElement("div", { className: "flex flex-col gap-y-[8px]" },
                React.createElement("label", { className: "text-black-400 font-N8p5" }, "GLOBAL INCIDENTS"),
                React.createElement("label", { className: "flex flex-row gap-x-2 items-center h-[40px] cursor-pointer" },
                    React.createElement("input", { type: "checkbox", className: "w-[20px] h-[20px] accent-green", checked: excludeGlobal, onChange: function (e) { return setExcludeGlobal(e.target.checked); } }),
                    React.createElement("span", { className: "font-N8 text-white" }, "Exclude")))),
        error != null && (React.createElement("label", { className: "font-N7 text-red" }, "Could not load incidents: ".concat(error.message))),
        React.createElement(StaticTable, { data: incidents !== null && incidents !== void 0 ? incidents : [], columnDefinitions: columns, loading: incidents == null && error == null, emptyMessage: siteId == null
                ? "No incidents match these filters."
                : "No incidents for this site.", buildRowLink: function (row) { return "/incident/".concat(row.uid); } }),
        canWrite && (React.createElement(React.Fragment, null,
            React.createElement(StyledButton, { text: siteId == null ? "CREATE GLOBAL INCIDENT" : "CREATE INCIDENT", className: "w-fit", style: StyledButtonStyle.Green, onClick: openCreate }),
            React.createElement(Dialog, { open: showCreate, onClose: closeCreate, className: "relative z-50" },
                React.createElement("div", { className: "fixed inset-0 bg-black/30", "aria-hidden": "true" }),
                React.createElement("div", { className: "fixed inset-0 flex items-center justify-center p-4" },
                    React.createElement(Dialog.Panel, { className: "mx-auto" }, showCreate && (React.createElement(IncidentEditDialog, { chargers: chargers !== null && chargers !== void 0 ? chargers : [], powernodes: powernodes !== null && powernodes !== void 0 ? powernodes : [], onClose: closeCreate, onSubmit: submitCreate })))))))));
};
